import { FC } from 'react';
import type { VisaoGeralMes } from '@/types/dashboards';
import { produzidoBase } from '@/utils/equiplexSop';
import type { MatrizDesvio } from '@/utils/equiplexVisaoGeral';
import { fmtData, fmtDesvio, fmtUn, mesCurto } from '@/utils/painelFormat';
import { type ModoTema, visualFaixa } from '../chartTheme';
import PainelCard from '../PainelCard';

interface Props {
  matriz: MatrizDesvio;
  meses: VisaoGeralMes[];
  mesSelecionado: string;
  limiares: { critico: number; alerta: number };
  modo: ModoTema;
  /** Último dia fechado: o mês em andamento só compara as OPs até aqui. */
  fechadoAte: string;
}

/**
 * Desvio % por máquina e mês. Cada célula traz ícone e número — a faixa (no plano,
 * alerta, crítico) nunca é só cor. A coluna do mês escolhido fica destacada.
 */
const DesvioMaquinaMatriz: FC<Props> = ({ matriz, meses, mesSelecionado, limiares, modo, fechadoAte }) => (
  <PainelCard
    testId="vg-matriz-desvio"
    titulo="Desvio % por máquina e mês"
    subtitulo={`Produzido contra programado. Alerta acima de ${limiares.alerta}%, crítico acima de ${limiares.critico}% (para mais ou para menos).`}
  >
    {matriz.maquinas.length === 0 ? (
      <div className="py-10 text-center text-sm text-content-muted">Nenhuma OP no período.</div>
    ) : (
      <div className="relative overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-theme-border text-left text-xs uppercase tracking-wide text-content-muted">
              <th className="py-2 pr-3 font-medium">Máquina</th>
              {meses.map((m) => (
                <th
                  key={m.key}
                  className={`whitespace-nowrap px-2 py-2 text-right font-medium ${m.key === mesSelecionado ? 'text-content-primary' : ''}`}
                  aria-current={m.key === mesSelecionado ? 'date' : undefined}
                >
                  {mesCurto(m.label)}
                  {m.em_andamento && '*'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matriz.maquinas.map((maq) => (
              <tr key={maq} className="border-b border-theme-border last:border-0">
                <th scope="row" className="whitespace-nowrap py-2 pr-3 text-left font-medium text-content-primary">
                  {maq}
                </th>
                {meses.map((m) => {
                  const c = matriz.celulas[maq]?.[m.key];
                  const destaque = m.key === mesSelecionado ? 'bg-surface-hover' : '';
                  if (!c) {
                    return (
                      <td key={m.key} className={`px-2 py-2 text-right text-content-muted ${destaque}`}>
                        —
                      </td>
                    );
                  }
                  const v = visualFaixa(c.faixa, modo);
                  return (
                    <td
                      key={m.key}
                      className={`whitespace-nowrap px-2 py-2 text-right tabular-nums text-content-primary ${destaque}`}
                      title={`${v.rotulo} · programado ${fmtUn(c.base)} · produzido ${fmtUn(produzidoBase(c))} · ${fmtUn(c.ops)} OPs`}
                    >
                      <span className="inline-flex items-center justify-end gap-1">
                        <span aria-hidden className="material-symbols-outlined text-[15px] leading-none" style={{ color: v.cor }}>
                          {v.icone}
                        </span>
                        {fmtDesvio(c.desvio_pct)}
                        <span className="sr-only"> ({v.rotulo})</span>
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-content-muted">
          * Mês em andamento: só as OPs com início até {fmtData(fechadoAte)} (dias fechados), dos dois lados.
        </p>
      </div>
    )}
  </PainelCard>
);

export default DesvioMaquinaMatriz;
